'use client';

import { useState, useEffect, useCallback } from 'react';

interface UseHashNavigationOptions {
  scrollOffset?: number;
}

interface UseHashNavigationReturn {
  hash: string;
  setHash: (newHash: string) => void;
  scrollToHash: (hash: string) => void;
}

/**
 * Hook for managing URL hash navigation
 * Tracks the current hash, provides functions to update it, and handles scrolling
 */
export function useHashNavigation(
  options: UseHashNavigationOptions = {}
): UseHashNavigationReturn {
  const { scrollOffset = 100 } = options;
  const [hash, setHashState] = useState('');

  // Initialize hash from URL on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentHash = window.location.hash.slice(1);
      setHashState(currentHash);
    }
  }, []);

  // Listen for hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const newHash = window.location.hash.slice(1);
      setHashState(newHash);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Scroll to element with given hash
  const scrollToHash = useCallback(
    (targetHash: string) => {
      if (!targetHash) return;

      const element = document.getElementById(targetHash);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - scrollOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    },
    [scrollOffset]
  );

  // Update URL hash and internal state
  const setHash = useCallback(
    (newHash: string) => {
      if (typeof window !== 'undefined') {
        // Update URL without triggering a page reload
        const url = new URL(window.location.href);
        url.hash = newHash;
        window.history.pushState(null, '', url.toString());
        setHashState(newHash);
      }
    },
    []
  );

  return { hash, setHash, scrollToHash };
}
